import React from 'react';
import trim from 'lodash/trim';
import isEmpty from 'validator/lib/isEmpty';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, Trans } from '@lyearn/i18n';
import { Fields, FieldType } from '@/components/Form';
import { isFilterGroupEmpty } from '@/components/Form/components/Conditions/utils';
import LegacyVisibilitySettingsFormField from '@/components/Form/components/VisibilitySettings/legacy/LegacyVisibilitySettingsFormField';
import { renderFormAssignmentRestriction } from '@/components/Form/renderers/renderAssignmentRestriction';
import { renderFilterGroup } from '@/components/Form/renderers/renderFilterGroup';
import { GroupTypeEnum } from '@/types/schema';

import { CreateUserGroupInput, FormSection } from '../../types';

export const getFormFields = (t: TFunction, type: GroupTypeEnum): Fields<CreateUserGroupInput> => {
  return {
    name: {
      name: 'name',
      placeholder:
        type === GroupTypeEnum.UserCollection
          ? t('Group:Editor.NamePlaceholder')
          : t('Group:Editor.CollectionNamePlaceholder'),
      label: t('Common:Form.Title'),
      type: FieldType.Text,
      size: 'large',
      props: {
        characterLimit: 100,
      },
    },
    description: {
      name: 'description',
      label: t('Common:Form.Description'),
      placeholder:
        type === GroupTypeEnum.UserCollection
          ? t('Group:Editor.DescriptionPlaceholder')
          : t('Group:Editor.CollectionDescriptionPlaceholder'),
      required: false,
      type: FieldType.Multiline,
      size: 'large',
      props: {
        characterLimit: 160,
        rows: 3,
      },
    },
    filterGroup: {
      name: 'filterGroup',
      required: false,
      type: FieldType.Custom,
      Component: renderFilterGroup({ type }),
    },
    contentVisibilitySettings: {
      name: 'contentVisibilitySettings',
      type: FieldType.Custom,
      Component: ({ onChange, value }) => {
        return <LegacyVisibilitySettingsFormField value={value!} onChange={onChange} />;
      },
    },
    assignmentRestriction: {
      name: 'assignmentRestriction',
      type: FieldType.Custom,
      Component: renderFormAssignmentRestriction({
        contentName: t('Group:Collection'),
      }),
    },
  };
};

export const getSectionLayout = (
  t: TFunction,
  type: GroupTypeEnum,
  enableAssignmentRestriction: boolean,
) =>
  getEnabledItems([
    {
      id: FormSection.Basic,
      layout: ['name', 'description'],
      enabled: true,
    },
    {
      id: FormSection.Condition,
      layout: ['filterGroup'],
      enabled: true,
    },
    {
      id: FormSection.AssignmentRestriction,
      layout: ['assignmentRestriction'],
      enabled: type === GroupTypeEnum.ContentCollection && enableAssignmentRestriction,
    },
    {
      id: FormSection.Visibility,
      layout: ['contentVisibilitySettings'],
      enabled: type === GroupTypeEnum.ContentCollection,
      name: t('Group:VisibilitySettings.Header'),
      description: t('Group:VisibilitySettings.Description'),
    },
  ]);

export const validateForm = async (values: CreateUserGroupInput) => {
  const errors: any = {};
  if (isEmpty(trim(values.name))) {
    errors.name = <Trans i18nKey="Group:Editor.NameEmptyError" />;
  }
  if (isFilterGroupEmpty(values.filterGroup)) {
    errors.filterGroup = <Trans i18nKey="Group:Editor.FilterGroupEmptyError" />;
  }

  return Promise.resolve(errors);
};
